/** @jsx jsx */
import { Box, Container, EmptyState, EmptyStateListWhite, Flex, Pagination } from "@eworkit/iris";
import React, { FC, memo, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { LoadingSpinner } from "@/components/Loading";
import { UserContext } from "@/components/User";
import { ListViewHeader } from "@/components/views";
import { IURLSortConfig } from "@/core/url";
import {
  applicationsPatchParamsType,
  fetchJobApplications,
  IApplicationURLFilter,
  IJobApplication,
  IPageableJobApplication,
  jobApplicationListSearchParamsType,
} from "@/data/job-applications";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { useDebounce } from "@/hooks/Timeout";
import { mapToQuery } from "@/infrastructure/Filter/service";
import { useRouter } from "@/router";
import { ApplicationFilters } from "@/views/job-applications/list/filters/ApplicationFilters";
import { useApplicationFilters } from "@/views/job-applications/list/filters/useApplicationFilters";
import { JobApplicationsList } from "@/views/job-applications/list/JobApplicationsList";

import { jobApplicationFiltersMapper } from "./filters/service";

export const JobApplicationsView: FC = memo(() => {
  const { t } = useTranslation("session");

  const { contextCompany, contextConsultantProfileId } = useContext(UserContext);

  //  const [isEmptyList, setIsEmpty] = useState(false);

  const debounceTime = 600;

  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: jobApplicationListSearchParamsType,
    ensureMatchParams: applicationsPatchParamsType,
  });

  const fetchApplicationsList: TPageableFetchFunction<
    IJobApplication,
    IPageableJobApplication,
    IApplicationURLFilter,
    IURLSortConfig[]
  > = useCallback(
    (params, options) => {
      const { filtersConfig, ...restParams } = params;

      return fetchJobApplications(
        {
          ...restParams,
          companyId: contextCompany && contextCompany.id,
          ...mapToQuery(jobApplicationFiltersMapper, filtersConfig || {}),
        },
        options,
      );
    },
    [contextCompany, contextConsultantProfileId],
  );

  /* VA-3440
  useEffect(() => {
    const handleFetchAll = async () => {
      return await fetchJobApplications({
        size: 0,
        companyId: contextCompany && contextCompany.id,
      });
    };
    handleFetchAll().then(res => {
      setIsEmpty(res.totalElements <= 0);
    });
  }, []); */

  const debouncedFilters = useDebounce(meta.filtersConfig, debounceTime);

  const mappedMeta = useMemo(() => {
    return {
      ...meta,
      filtersConfig: debouncedFilters,
    };
  }, [meta, debouncedFilters]);

  const { working, payload, changeFilter, changePage, changePageSize, changeSort } = useMetaPageableFetcher(
    fetchApplicationsList,
    {
      meta: mappedMeta,
      changeMeta,
    },
    [JSON.stringify(mappedMeta), fetchApplicationsList],
  );

  const { handleFiltersChange, clearFilters } = useApplicationFilters(meta.filtersConfig, changeFilter);

  const noMatchingApplications = useMemo(() => {
    return !working && !payload?.content.length;
  }, [working, payload?.content, meta.filtersConfig]);

  /* isEmptyList ? (
    <Box sx={{ py: 120 }} px="sm">
      <EmptyState
        icon={<EmptyStateListWhite />}
        title={t("JOB_APPLICATIONS.NO_APPLICATIONS")}
        content={t("JOB_APPLICATIONS.NO_APPLICATIONS_DESCRIPTION")}
      />
    </Box>
  ) :*/
  return (
    <Flex flexDirection="column" gap={["xs", null, "sm", "md"]} pb="lg">
      <ListViewHeader header={t("JOB_APPLICATIONS.LIST.SECTION_HEADER")} count={payload?.totalElements}>
        <Flex gap="xs">
          <ApplicationFilters
            onFiltersUpdate={handleFiltersChange}
            onSortUpdate={changeSort}
            onFilterClear={clearFilters}
            filtersValue={meta.filtersConfig}
            sortValue={meta.sortConfig}
            applicationCount={payload?.totalElements}
          />
        </Flex>
      </ListViewHeader>
      {noMatchingApplications ? (
        <Box sx={{ py: 120 }} px="sm">
          <EmptyState
            icon={<EmptyStateListWhite />}
            title={t("JOB_APPLICATIONS.NO_MATCHING_APPLICATIONS")}
            content={t("JOB_APPLICATIONS.NO_MATCHING_APPLICATIONS_DESCRIPTION")}
          />
        </Box>
      ) : (
        <React.Fragment>
          <Container>
            <Flex px={["0", null, "sm"]} flexDirection="column" gap="lg">
              {working && (
                <Flex justifyContent="center" sx={{ py: 120 }}>
                  <LoadingSpinner size="lg" variant="primary" />
                </Flex>
              )}
              {payload && !working && <JobApplicationsList jobApplications={payload.content.map(item => item.data)} />}
              {payload && !working && (
                <Pagination
                  pagesCount={payload.totalPages}
                  activeIndex={payload.number}
                  onChangeIndex={changePage}
                  pageSize={payload.size}
                  onChangePageSize={changePageSize}
                  resultsLabel={t("public:COMPONENTS.PAGINATION.RESULTS")}
                />
              )}
            </Flex>
          </Container>
        </React.Fragment>
      )}
    </Flex>
  );
});
